import {
  DayOfWeek,
  MetricField,
  MetricUnit,
  ReportScheduleRecurrence as Recurrence,
} from '@/__generated__/graphql';
import { DayjsSchema } from '@/utils/zod';
import z from 'zod/v4';

const MetricSchema = z.object({
  name: z.enum(MetricField),
  unit: z.enum(MetricUnit),
});

const ScheduleSchema = z
  .object({
    recurrence: z.enum(Recurrence),
    time: DayjsSchema,
    dayOfWeek: z.enum(DayOfWeek).nullish(),
    daysOfWeek: z.array(z.enum(DayOfWeek)).nullish(),
  })
  .refine((data) => data.recurrence !== Recurrence.Weekly || data.daysOfWeek?.length, {
    message: 'Debes seleccionar al menos un día de la semana.',
    path: ['daysOfWeek'],
  })
  .refine(
    (data) =>
      ![Recurrence.MonthlyFirstDay, Recurrence.MonthlyLastDay].includes(data.recurrence) ||
      data.dayOfWeek != null,
    {
      message: 'Debes seleccionar un día de la semana.',
      path: ['dayOfWeek'],
    },
  );

const EmailSchema = z.object({
  email: z.email(),
});

const FirstStepSchema = z.object({
  devices: z.array(z.string().min(1)).min(1),
  metrics: z.array(MetricSchema).min(1),
});

const SecondStepSchema = z.object({
  period: z.object({
    start: z.string(),
    stop: z.string().nullable(),
  }),
  meanWindow: z.string().nullable(),
  timezone: z.string(),
});

const ThirdStepSchema = z
  .object({
    createPdf: z.boolean(),
    createExcel: z.boolean(),
    emails: z.array(EmailSchema).min(1),
  })
  .check(({ value, issues }) => {
    if (!value.createExcel && !value.createPdf) {
      issues.push({
        code: 'custom',
        message: 'Debes seleccionar al menos un formato',
        input: value.createPdf,
        path: ['createPdf'],
      });

      issues.push({
        code: 'custom',
        message: 'Debes seleccionar al menos un formato',
        input: value.createExcel,
        path: ['createExcel'],
      });
    }
  });

const FourthStepSchema = z.object({
  enabled: z.boolean(),
  schedule: ScheduleSchema.optional(),
});

const FifthStepSchema = z
  .object({
    saveTemplate: z.boolean(),
    templateName: z.string().optional(),
    templateDescription: z.string().optional(),
  })
  .refine((data) => !data.saveTemplate || data.templateName?.length, {
    error: 'Debes proveer un nombre para guardar el reporte',
    path: ['templateName'],
  });

export const CreateReportSchema = z.object({
  templateId: z.int().optional(),
  firstStep: FirstStepSchema,
  secondStep: SecondStepSchema,
  thirdStep: ThirdStepSchema,
  fourthStep: FourthStepSchema,
  fifthStep: FifthStepSchema,
});

export type CreateReportFieldValues = z.output<typeof CreateReportSchema>;
